import { BottomSheetView } from '@gorhom/bottom-sheet'
import { View } from 'react-native'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import GridFilterSimpleSwitch from '@/components/filtering/GridFilterBasicSwitch'
import TourSpot from '@/components/tour/TourSpot'
import UniButton from '@/components/unistyles/UniButton'
import UniDropdown from '@/components/unistyles/UniDropdown'
import UniSegmentedButtons from '@/components/unistyles/UniSegmentedButtons'
import Species from '@/enums/species'
import { useAppStore } from '@/store/useAppStore'
import trpc from '@/utils/trpc'

const GridFilterView = () => {
  const { theme } = useUnistyles()
  const basicFilters = useAppStore.use.basicFilters()
  const resetBasicFilters = useAppStore.use.resetBasicFilters()
  const setBasicFilters = useAppStore.use.setBasicFilters()

  const advancedFilters = useAppStore.use.advancedFilters()
  const setAdvancedFilters = useAppStore.use.setAdvancedFilters()

  const currentOrganization = useAppStore.use.currentOrganization().organization

  const users = trpc.user.list.useQuery(
    {
      organizationId: currentOrganization?.id || '',
      includeDeactivated: true,
    },
    {
      enabled: !!currentOrganization,
    }
  )

  const volunteerList =
    users.data?.map((user) => ({
      label: `${user.firstName} ${user.lastName}`,
      value: user.id,
    })) || []

  return (
    <BottomSheetView style={styles.root} testID="grid-filter-basic-view">
      <TourSpot id="foster-grid-filter-basic" style={styles.innerRoot}>
        <View style={styles.resetButtonRow}>
          <TourSpot id="foster-grid-filter-reset">
            <UniButton
              disabled={!basicFilters.isDirty}
              icon="restore"
              onPress={() => resetBasicFilters()}
            >
              Reset Filters
            </UniButton>
          </TourSpot>

          <TourSpot id="foster-grid-filter-advanced">
            <UniButton
              icon="filter-variant"
              onPress={() => {
                setBasicFilters({
                  ...basicFilters,
                  isActive: false,
                })
                setAdvancedFilters({
                  ...advancedFilters,
                  isActive: true,
                })
              }}
            >
              Advanced Filters
            </UniButton>
          </TourSpot>
        </View>
        <>
          <View style={styles.segmentedButtons}>
            <TourSpot id="foster-grid-filter-view-all">
              <UniSegmentedButtons
                buttons={[
                  {
                    icon: 'account',
                    value: 'mine',
                    label: 'My Fosters',
                    testID: 'my-fosters-button',
                  },
                  {
                    icon: 'account-group',
                    value: 'all',
                    label: 'All Fosters',
                    testID: 'all-fosters-button',
                  },
                ]}
                onValueChange={(value: string) => {
                  setBasicFilters({
                    ...basicFilters,
                    filters: {
                      ...basicFilters.filters,
                      viewAll: value === 'all',
                    },
                  })
                }}
                // style={styles.segmentedButtons}
                value={basicFilters.filters.viewAll ? 'all' : 'mine'}
              />
            </TourSpot>
          </View>
          {basicFilters.filters.viewAll && (
            <UniDropdown
              activeColor={theme.colors.primaryContainer}
              containerStyle={styles.dropdownContainer}
              data={[
                { label: 'All Volunteers', value: undefined },
                ...volunteerList,
              ]}
              itemTextStyle={styles.text}
              labelField={'label'}
              onChange={(selected) => {
                setBasicFilters({
                  ...basicFilters,
                  filters: {
                    ...basicFilters.filters,
                    volunteerId: selected.value,
                  },
                })
              }}
              placeholder="Filter by Volunteer"
              placeholderStyle={styles.text}
              search
              searchField="label"
              searchPlaceholder="Search for a volunteer"
              selectedTextStyle={[styles.text, styles.selectedText]}
              style={styles.filterContainer}
              value={basicFilters.filters.volunteerId}
              valueField={'value'}
            />
          )}
          <UniDropdown
            activeColor={theme.colors.primaryContainer}
            containerStyle={styles.dropdownContainer}
            data={[
              { label: 'All Species', value: undefined },
              { label: 'Dogs', value: Species.Dog },
              { label: 'Cats', value: Species.Cat },
            ]}
            itemTextStyle={styles.text}
            labelField={'label'}
            onChange={(selected) => {
              setBasicFilters({
                ...basicFilters,
                filters: {
                  ...basicFilters.filters,
                  species: selected.value,
                },
              })
            }}
            placeholder="Species"
            placeholderStyle={styles.text}
            selectedTextStyle={[styles.text, styles.selectedText]}
            style={styles.filterContainer}
            value={basicFilters.filters.species}
            valueField={'value'}
          />
          <GridFilterSimpleSwitch
            filter="currentlyFostered"
            label="Currently Fostered"
            style={styles.filterContainer}
            textStyle={styles.text}
          />
          <GridFilterSimpleSwitch
            filter="readyForAdoption"
            label="Ready for Adoption"
            style={styles.filterContainer}
            textStyle={styles.text}
          />
          <GridFilterSimpleSwitch
            filter="includeDeceased"
            label="Include Deceased"
            style={styles.filterContainer}
            textStyle={styles.text}
          />
        </>
      </TourSpot>
    </BottomSheetView>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    dropdownContainer: {
      backgroundColor: theme.colors.background,
      borderColor: theme.colors.background,
    },
    filterContainer: {
      alignItems: 'center',
      borderRadius: theme.tokens.containerBorderRadius,
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingVertical: theme.tokens.spacing[4],
    },
    innerRoot: {
      paddingBottom: theme.tokens.spacing[6],
      paddingHorizontal: theme.tokens.spacing[6],
    },
    resetButtonRow: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    root: {
      backgroundColor: theme.colors.surface,
    },
    segmentedButtons: {
      marginBottom: theme.tokens.spacing[3],
      marginTop: theme.tokens.spacing[4],
    },
    selectedText: {
      backgroundColor: theme.colors.surface,
    },
    text: {
      color: theme.colors.onBackground,
      fontSize: 16,
    },
  }
})

export default GridFilterView
